import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Share, Plus, Smartphone, MoreVertical } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop'); // 'ios', 'android', 'desktop'
  const [isInstalled, setIsInstalled] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('install-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 3) return;
    }

    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    
    if (isIOS) {
      setDeviceType('ios');
      setTimeout(() => setShowPrompt(true), 2000);
    } else if (isAndroid) {
      setDeviceType('android');
      // Listen for beforeinstallprompt event
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShowPrompt(true), 2000);
      };
      window.addEventListener('beforeinstallprompt', handler);
      
      // If no prompt after 3 seconds, show manual instructions
      setTimeout(() => {
        if (!deferredPrompt) {
          setShowPrompt(true);
        }
      }, 3000);
      
      return () => window.removeEventListener('beforeinstallprompt', handler);
    } else {
      setDeviceType('desktop');
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowPrompt(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      
      // Show for desktop after 2 seconds anyway
      setTimeout(() => setShowPrompt(true), 2000);
      
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else if (deviceType === 'android') {
      // Show manual steps for Android
      setShowSteps(true);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('install-prompt-dismissed', new Date().toISOString());
  };

  if (isInstalled || !showPrompt) return null;

  // iOS Instructions
  const IOSInstructions = () => (
    <div className="space-y-3 bg-slate-50 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
        <div>
          <p className="text-sm font-medium">Tryck på dela-knappen</p>
          <p className="text-xs text-muted-foreground">Fyrkant med pil uppåt (längst ner i Safari)</p>
          <div className="mt-2 inline-flex items-center gap-1 bg-white border rounded-lg px-3 py-1.5">
            <Share className="w-4 h-4 text-blue-500" />
            <span className="text-xs">Dela</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
        <div>
          <p className="text-sm font-medium">Scrolla och tryck "Lägg till på hemskärmen"</p>
          <div className="mt-2 inline-flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5">
            <Plus className="w-4 h-4 text-gray-600" />
            <span className="text-xs">Lägg till på hemskärmen</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold shrink-0">✓</div>
        <div>
          <p className="text-sm font-medium">Klart!</p>
          <p className="text-xs text-muted-foreground">Livon finns nu på din hemskärm</p>
        </div>
      </div>
    </div>
  );

  // Android Instructions
  const AndroidInstructions = () => (
    <div className="space-y-3 bg-slate-50 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
        <div>
          <p className="text-sm font-medium">Tryck på menyn (⋮)</p>
          <p className="text-xs text-muted-foreground">Tre prickar uppe till höger i Chrome</p>
          <div className="mt-2 inline-flex items-center gap-1 bg-white border rounded-lg px-3 py-1.5">
            <MoreVertical className="w-4 h-4 text-gray-600" />
            <span className="text-xs">Meny</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
        <div>
          <p className="text-sm font-medium">Tryck "Lägg till på startskärmen"</p>
          <p className="text-xs text-muted-foreground">Eller "Installera app" om det visas</p>
          <div className="mt-2 inline-flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5">
            <Plus className="w-4 h-4 text-gray-600" />
            <span className="text-xs">Lägg till på startskärmen</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold shrink-0">✓</div>
        <div>
          <p className="text-sm font-medium">Klart!</p>
          <p className="text-xs text-muted-foreground">Livon finns nu på din startskärm</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] bg-white border border-border rounded-2xl shadow-xl z-50 animate-slide-up overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-blue-50 px-4 py-3 flex items-center justify-between border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Installera Livon</h3>
            <p className="text-xs text-muted-foreground">
              {deviceType === 'ios' && 'Få appen på din iPhone/iPad'}
              {deviceType === 'android' && 'Få appen på din Android'}
              {deviceType === 'desktop' && 'Få appen på din enhet'}
            </p>
          </div>
        </div>
        <button 
          onClick={handleDismiss}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-black/5 rounded-full transition-colors"
          aria-label="Stäng"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {/* iOS Device */}
        {deviceType === 'ios' && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              <strong>Installera Livon som en app:</strong>
            </p>
            
            {!showSteps ? (
              <Button 
                onClick={() => setShowSteps(true)}
                className="w-full rounded-xl h-12 text-base"
              >
                Visa instruktioner
              </Button>
            ) : (
              <IOSInstructions />
            )}
            
            <button 
              onClick={handleDismiss}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground py-2"
            >
              Påminn mig senare
            </button>
          </div>
        )}

        {/* Android Device */}
        {deviceType === 'android' && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              <strong>Installera Livon som en app:</strong>
            </p>
            
            {deferredPrompt && !showSteps ? (
              // Native install prompt available
              <div className="flex gap-3">
                <Button 
                  onClick={handleInstall}
                  className="flex-1 rounded-xl h-12 text-base"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Installera nu
                </Button>
                <Button 
                  onClick={handleDismiss}
                  variant="outline"
                  className="rounded-xl h-12"
                >
                  Senare
                </Button>
              </div>
            ) : !showSteps ? (
              // Show manual instructions button
              <Button 
                onClick={() => setShowSteps(true)}
                className="w-full rounded-xl h-12 text-base"
              >
                Visa instruktioner
              </Button>
            ) : (
              <AndroidInstructions />
            )}
            
            {showSteps && (
              <button 
                onClick={handleDismiss}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground py-2"
              >
                Påminn mig senare
              </button>
            )}
          </div>
        )}

        {/* Desktop */}
        {deviceType === 'desktop' && (
          <div className="space-y-4">
            {deferredPrompt ? (
              <>
                <p className="text-sm text-foreground">
                  <strong>Installera Livon på din dator!</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  Få snabb åtkomst direkt från skrivbordet.
                </p>
                <div className="flex gap-3">
                  <Button 
                    onClick={handleInstall}
                    className="flex-1 rounded-xl h-12 text-base"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Installera nu
                  </Button>
                  <Button 
                    onClick={handleDismiss}
                    variant="outline"
                    className="rounded-xl h-12"
                  >
                    Senare
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-foreground">
                  <strong>Lägg till Livon på hemskärmen:</strong>
                </p>
                <div className="space-y-3 bg-slate-50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                    <div>
                      <p className="text-sm font-medium">Öppna webbläsarmenyn</p>
                      <p className="text-xs text-muted-foreground">Tre prickar (Chrome) eller delningsikon (Safari)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                    <div>
                      <p className="text-sm font-medium">Välj "Installera app" eller "Lägg till på hemskärmen"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold shrink-0">&#10003;</div>
                    <div>
                      <p className="text-sm font-medium">Klart!</p>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleDismiss}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground py-2"
                >
                  Påminn mig senare
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
